import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { format } from 'date-fns';
import { sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { RosterDay } from '../../types';
import { Employee } from './types';
import { Interval, PlanUnit } from 'components/types';

import { getCellDateClasses } from '../../utils';
import { getHoursMinutesSeconds } from 'utils/date';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import { FontAwesomeSvgIcon, Tooltip } from 'components/shared';

import Duty from '../Duty';
import Absence from './Absence';

const EmployeeRowStyles = styled(TableRow)`
  position: relative;
  page-break-inside: avoid;
  break-inside: avoid;

  .cell-content {
    height: 100%;
  }

  .croppedTextCell {
    text-overflow: ellipsis;
    background-color: inherit;

    span {
      float: none;
      padding-right: 0;
    }

    &:hover {
      text-overflow: unset;
    }

    .chevron-icon {
      font-size: 10px;
      margin: 0 4px;
      vertical-align: middle;
    }
  }
`;

type Props = {
  employee: Employee;
  selectedPlanUnits: PlanUnit[];
  rosterDays: RosterDay[];
  portal: string;
  interval: Interval;
};

export default function EmployeeRow({
  employee,
  selectedPlanUnits,
  rosterDays,
  portal,
  interval
}: Props) {
  const {
    benefit,
    nameInformal,
    plannedCostCenter,
    days,
    phoneNumber,
    smsNumber
  } = employee;
  const { t } = useTranslation('common');

  return (
    <EmployeeRowStyles className="employee-row">
      <TableCell>
        <Tooltip
          title={
            <Table size="small" sx={{ 'tr:last-child td': { border: 0 } }}>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <strong>{t('source')}</strong>
                  </TableCell>
                  <TableCell>{benefit.costCenter?.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>{t('work')}</strong>
                  </TableCell>
                  <TableCell>{plannedCostCenter.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>{t('function')}</strong>
                  </TableCell>
                  <TableCell>{benefit.function?.name || '-'}</TableCell>
                </TableRow>
                {smsNumber && (
                  <TableRow>
                    <TableCell>
                      <strong>{t('mobileNumber')}</strong>
                    </TableCell>
                    <TableCell>{smsNumber}</TableCell>
                  </TableRow>
                )}
                {phoneNumber && (
                  <TableRow>
                    <TableCell>
                      <strong>{t('phoneNumber')}</strong>
                    </TableCell>
                    <TableCell>{phoneNumber}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          }
          arrow
          enterDelay={300}
          enterNextDelay={300}
          placement="right"
          disableInteractive
        >
          <div className="cell-content">
            <div className="function-container semibold">
              {benefit.function?.colorCode && (
                <span
                  className="function-color"
                  style={{ color: benefit.function.colorCode }}
                >
                  <FontAwesomeSvgIcon
                    color="inherit"
                    icon={faCircle}
                    fontSize="small"
                  />
                </span>
              )}
              {nameInformal}
            </div>
            {selectedPlanUnits.length === 1 ? (
              selectedPlanUnits[0].id !== benefit.costCenter?.id && (
                <div>{benefit.costCenter?.name}</div>
              )
            ) : (
              <div className="cost-center-row croppedTextCell">
                <span>
                  {benefit.costCenter?.name}
                  {benefit.costCenter?.id !== plannedCostCenter?.id && (
                    <>
                      <FontAwesomeSvgIcon
                        icon={faChevronRight}
                        fontSize="small"
                        className="chevron-icon"
                      />
                      {plannedCostCenter?.name}
                    </>
                  )}
                </span>
              </div>
            )}
          </div>
        </Tooltip>
      </TableCell>
      {rosterDays.map(({ day, date }) => {
        const absences = days[format(date, 'yyyy-MM-dd')]?.absences;
        const duties = days[format(date, 'yyyy-MM-dd')]?.duties;

        return (
          <TableCell key={day} className={getCellDateClasses({ date })}>
            {absences &&
              sortBy(absences, [
                (absence) => getHoursMinutesSeconds(absence.start),
                (absence) => getHoursMinutesSeconds(absence.end)
              ]).map((absence) => (
                <Absence
                  key={absence.id}
                  absence={absence}
                  date={date}
                  interval={interval}
                />
              ))}
            {duties &&
              sortBy(duties, [
                (duty) => getHoursMinutesSeconds(duty.start),
                (duty) => getHoursMinutesSeconds(duty.end)
              ]).map((duty) => (
                <Duty key={duty.dutyLineId} duty={duty} portal={portal} />
              ))}
          </TableCell>
        );
      })}
    </EmployeeRowStyles>
  );
}
